import React, { createContext, useState, useEffect, ReactNode } from 'react';
import {
  NotificationPreferences,
  DEFAULT_NOTIFICATION_PREFERENCES,
  NotificationEventType,
} from '../types/notificationPreferences';

const STORAGE_KEY = 'notification_preferences';

interface NotificationPreferencesContextType {
  preferences: NotificationPreferences;
  updatePreferences: (updates: Partial<NotificationPreferences>) => void;
  resetPreferences: () => void;
  shouldShowNotification: (eventType?: NotificationEventType) => boolean;
  isInDndMode: () => boolean;
}

export const NotificationPreferencesContext = createContext<NotificationPreferencesContextType>({
  preferences: DEFAULT_NOTIFICATION_PREFERENCES,
  updatePreferences: () => {},
  resetPreferences: () => {},
  shouldShowNotification: () => true,
  isInDndMode: () => false,
});

interface NotificationPreferencesProviderProps {
  children: ReactNode;
}

export const NotificationPreferencesProvider: React.FC<NotificationPreferencesProviderProps> = ({
  children,
}) => {
  const [preferences, setPreferences] = useState<NotificationPreferences>(() => {
    // Load preferences from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        return { ...DEFAULT_NOTIFICATION_PREFERENCES, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    }
    return DEFAULT_NOTIFICATION_PREFERENCES;
  });

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    }
  }, [preferences]);

  const updatePreferences = (updates: Partial<NotificationPreferences>) => {
    setPreferences((prev) => ({
      ...prev,
      ...updates,
      // Ensure nested objects are properly merged
      events: updates.events ? { ...prev.events, ...updates.events } : prev.events,
      dnd: updates.dnd ? { ...prev.dnd, ...updates.dnd } : prev.dnd,
    }));
  };

  const resetPreferences = () => {
    setPreferences(DEFAULT_NOTIFICATION_PREFERENCES);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isInDndMode = (): boolean => {
    if (!preferences.dnd.enabled) {
      return false;
    }

    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;

    const { startTime, endTime } = preferences.dnd;

    // Check if current time is within DND period
    // Handle case where DND period spans midnight
    if (startTime > endTime) {
      // DND period crosses midnight (e.g., 22:00 - 08:00)
      return currentTime >= startTime || currentTime < endTime;
    } else {
      // DND period is within same day (e.g., 13:00 - 15:00)
      return currentTime >= startTime && currentTime < endTime;
    }
  };

  const shouldShowNotification = (eventType?: NotificationEventType): boolean => {
    // Check if notifications are globally enabled
    if (!preferences.enabled) {
      return false;
    }

    // Check if in DND mode
    if (isInDndMode()) {
      return false;
    }

    // Check event-specific preferences if event type provided
    if (eventType) {
      const eventPreferences = preferences.events;
      switch (eventType) {
        case 'access_granted':
          return eventPreferences.accessGranted;
        case 'access_denied':
          return eventPreferences.accessDenied;
        case 'door_events':
          return eventPreferences.doorEvents;
        case 'device_alerts':
          return eventPreferences.deviceAlerts;
        case 'system_alerts':
          return eventPreferences.systemAlerts;
        default:
          return true;
      }
    }

    return true;
  };

  const value: NotificationPreferencesContextType = {
    preferences,
    updatePreferences,
    resetPreferences,
    shouldShowNotification,
    isInDndMode,
  };

  return (
    <NotificationPreferencesContext.Provider value={value}>
      {children}
    </NotificationPreferencesContext.Provider>
  );
};
